import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { actions as storesActions } from 'app/main/apps/stores/ducks/stores.slice';
import { DEFAULT_DASHBOARD_PRESET, DEFAULT_REVIEWS_ROWS_PER_PAGE } from '../constants';
import {
  AiDraftState,
  DashboardComparison,
  DashboardTabKey,
  KeywordRankingsPayload,
  LocationPatch,
  PostsTabKey,
  ReviewsFilter,
  StatsRange,
  StoreServiceAreaPayload,
  UberallAiPostPayload,
  UberallAiSettingsPayload,
  UberallAnalyticsPayload,
  UberallAutoResponseRuleListPayload,
  UberallDataPointPayload,
  UberallDataPointsPagePayload,
  UberallKeywordsPayload,
  UberallListingsSummaryPayload,
  UberallLocationProfilePayload,
  UberallPhotosPayload,
  UberallReplyTemplatePayload,
  UberallScorePayload,
  UberallSeoReportPayload,
  UberallSeoReportSentPayload,
  UberallSocialConnectionsPayload,
  UberallStoreLinkPayload,
  VisibilityState,
} from '../type';
import { rangeFromPreset } from '../utils/visibility.utils';
import {
  deleteAutoResponseRuleThunk,
  deletePhotoThunk,
  deletePostThunk,
  deleteReplyTemplateThunk,
  deleteServiceAreaThunk,
  disconnectSocialThunk,
  downloadSeoReportThunk,
  fetchAiSettingsThunk,
  fetchAutoResponseRulesThunk,
  fetchDashboardThunk,
  fetchKeywordRankingsThunk,
  fetchKeywordsThunk,
  fetchListingsThunk,
  fetchPhotosThunk,
  fetchPostDirectoriesThunk,
  fetchPostsThunk,
  fetchProfileThunk,
  fetchReplyTemplatesThunk,
  fetchReviewReplyThunk,
  fetchReviewsThunk,
  fetchScoreThunk,
  fetchServiceAreasThunk,
  fetchSocialConnectionsThunk,
  fetchUnansweredCountThunk,
  fetchVisibilityLinkThunk,
  generateAiPostThunk,
  generateAiReplyThunk,
  generateSeoReportThunk,
  markReviewReadThunk,
  publishReplyThunk,
  REVIEW_GONE_CODES,
  saveAiSettingsThunk,
  saveAutoResponseRuleThunk,
  saveKeywordsThunk,
  savePostThunk,
  saveProfileThunk,
  saveReplyTemplateThunk,
  saveServiceAreaThunk,
  sendSeoReportThunk,
  syncListingsThunk,
  syncServiceAreasToUberallThunk,
  uploadPhotoThunk,
} from './visibility.thunk';

const { setCurrentStoreId } = storesActions;

const emptySlot = { isLoading: false, hasError: false, hasLoaded: false };

const createInitialState = (): VisibilityState => ({
  loadedForStoreId: null,
  link: { data: null, ...emptySlot, requestId: null },
  score: { data: null, ...emptySlot },
  profile: { data: null, ...emptySlot, draft: {}, isSaving: false },
  listings: { data: null, ...emptySlot, isSyncing: false },
  photos: { data: null, ...emptySlot, isUploading: false },
  keywords: { data: null, ...emptySlot, isMutating: false },
  report: { data: null, ...emptySlot, isGenerating: false, isSending: false, isDownloading: false },
  dashboard: {
    data: null,
    ...emptySlot,
    range: rangeFromPreset(DEFAULT_DASHBOARD_PRESET),
    comparison: { preset: 'PREVIOUS_PERIOD', from: null, to: null },
    tab: 'overview',
    requestId: null,
  },
  keywordRankings: { data: null, ...emptySlot, requestId: null },
  postDirectories: { data: null, ...emptySlot, requestId: null },
  socialConnections: { data: null, ...emptySlot, requestId: null, isMutating: false },
  reviews: {
    data: [],
    ...emptySlot,
    count: 0,
    unansweredCount: 0,
    hasCountLoaded: false,
    page: 1,
    size: DEFAULT_REVIEWS_ROWS_PER_PAGE,
    filter: { answered: null, from: null, to: null },
    selectedId: null,
    requestId: null,
    pendingReplyById: {},
    replyErrorById: {},
    replyLoadRequestedById: {},
    aiDraftById: {},
    generatingFor: {},
    goneIds: [],
  },
  templates: { data: [], ...emptySlot, isMutating: false },
  serviceAreas: { data: [], ...emptySlot, isMutating: false, isSyncing: false },
  autoResponses: { data: [], ...emptySlot, count: 0, isMutating: false },
  aiSettings: { data: null, ...emptySlot, isSaving: false },
  posts: {
    data: [],
    ...emptySlot,
    count: 0,
    tab: 'composer',
    isMutating: false,
    isGeneratingDraft: false,
    hasDraftError: false,
    aiDraft: null,
  },
});

const initialState = createInitialState();

const visibilitySlice = createSlice({
  name: 'visibility',
  initialState,
  reducers: {
    updateProfileDraft: (state, action: PayloadAction<LocationPatch>) => {
      state.profile.draft = { ...state.profile.draft, ...action.payload };
    },
    discardProfileDraft: state => {
      state.profile.draft = {};
    },

    setDashboardRange: (state, action: PayloadAction<StatsRange>) => {
      state.dashboard.range = action.payload;
    },
    setDashboardComparison: (state, action: PayloadAction<DashboardComparison>) => {
      state.dashboard.comparison = action.payload;
    },
    setDashboardTab: (state, action: PayloadAction<DashboardTabKey>) => {
      state.dashboard.tab = action.payload;
    },

    setReviewsPage: (state, action: PayloadAction<number>) => {
      state.reviews.page = action.payload;
    },
    setReviewsPageSize: (state, action: PayloadAction<number>) => {
      state.reviews.size = action.payload;
      state.reviews.page = 1;
    },
    setReviewsFilter: (state, action: PayloadAction<Partial<ReviewsFilter>>) => {
      state.reviews.filter = { ...state.reviews.filter, ...action.payload };
      state.reviews.page = 1;
    },
    selectReview: (state, action: PayloadAction<number | null>) => {
      state.reviews.selectedId = action.payload;
    },
    setPendingReply: (state, action: PayloadAction<{ datapointId: number; text: string }>) => {
      state.reviews.pendingReplyById[action.payload.datapointId] = action.payload.text;
      state.reviews.replyErrorById[action.payload.datapointId] = false;
    },
    clearAiDraft: (state, action: PayloadAction<number>) => {
      delete state.reviews.aiDraftById[action.payload];
    },

    setPostsTab: (state, action: PayloadAction<PostsTabKey>) => {
      state.posts.tab = action.payload;
    },
    clearPostAiDraft: state => {
      state.posts.aiDraft = null;
    },
  },
  extraReducers: builder => {
    builder.addCase(setCurrentStoreId, (state, action: PayloadAction<string>) => {
      if (state.loadedForStoreId === action.payload) return state;
      const fresh = createInitialState();

      return {
        ...fresh,
        loadedForStoreId: action.payload,
        dashboard: {
          ...fresh.dashboard,
          range: state.dashboard.range,
          comparison: state.dashboard.comparison,
          tab: state.dashboard.tab,
        },
        reviews: { ...fresh.reviews, size: state.reviews.size },
        posts: { ...fresh.posts, tab: state.posts.tab },
      };
    });

    builder.addCase(fetchVisibilityLinkThunk.pending, (state, action) => {
      state.link.isLoading = true;
      state.link.hasError = false;
      state.link.requestId = action.meta.requestId;
    });
    builder.addCase(fetchVisibilityLinkThunk.fulfilled, (state, action) => {
      if (state.link.requestId !== action.meta.requestId) return;
      const link = action.payload as UberallStoreLinkPayload;
      state.link.data = link;
      state.link.isLoading = false;
      state.link.hasLoaded = true;
      if (link.ai) {
        state.aiSettings.data = link.ai;
        state.aiSettings.hasLoaded = true;
      }
      state.templates.data = link.replyTemplates ?? [];
      state.templates.hasLoaded = true;
    });
    builder.addCase(fetchVisibilityLinkThunk.rejected, (state, action) => {
      if (state.link.requestId !== action.meta.requestId) return;
      state.link.isLoading = false;
      state.link.hasError = true;
      state.link.hasLoaded = true;
    });

    builder.addCase(fetchScoreThunk.pending, state => {
      state.score.isLoading = true;
      state.score.hasError = false;
    });
    builder.addCase(fetchScoreThunk.fulfilled, (state, action: PayloadAction<UberallScorePayload>) => {
      state.score.data = action.payload;
      state.score.isLoading = false;
      state.score.hasLoaded = true;
    });
    builder.addCase(fetchScoreThunk.rejected, state => {
      state.score.isLoading = false;
      state.score.hasError = true;
      state.score.hasLoaded = true;
    });

    builder.addCase(fetchProfileThunk.pending, state => {
      state.profile.isLoading = true;
      state.profile.hasError = false;
    });
    builder.addCase(fetchProfileThunk.fulfilled, (state, action: PayloadAction<UberallLocationProfilePayload>) => {
      state.profile.data = action.payload;
      state.profile.isLoading = false;
      state.profile.hasLoaded = true;
    });
    builder.addCase(fetchProfileThunk.rejected, state => {
      state.profile.isLoading = false;
      state.profile.hasError = true;
      state.profile.hasLoaded = true;
    });
    builder.addCase(saveProfileThunk.pending, state => {
      state.profile.isSaving = true;
    });
    builder.addCase(saveProfileThunk.fulfilled, (state, action: PayloadAction<UberallLocationProfilePayload>) => {
      state.profile.data = action.payload;
      state.profile.draft = {};
      state.profile.isSaving = false;
    });
    builder.addCase(saveProfileThunk.rejected, state => {
      state.profile.isSaving = false;
    });

    builder.addCase(generateSeoReportThunk.pending, state => {
      state.report.isGenerating = true;
    });
    builder.addCase(generateSeoReportThunk.fulfilled, (state, action: PayloadAction<UberallSeoReportPayload>) => {
      state.report.data = action.payload;
      state.report.isGenerating = false;
      state.report.hasLoaded = true;
      if (state.link.data) {
        state.link.data.lastReportGeneratedAt = action.payload.generatedAt;
      }
    });
    builder.addCase(generateSeoReportThunk.rejected, state => {
      state.report.isGenerating = false;
    });

    builder.addCase(downloadSeoReportThunk.pending, state => {
      state.report.isDownloading = true;
    });
    builder.addCase(downloadSeoReportThunk.fulfilled, state => {
      state.report.isDownloading = false;
    });
    builder.addCase(downloadSeoReportThunk.rejected, state => {
      state.report.isDownloading = false;
    });

    builder.addCase(sendSeoReportThunk.pending, state => {
      state.report.isSending = true;
    });
    builder.addCase(sendSeoReportThunk.fulfilled, (state, action: PayloadAction<UberallSeoReportSentPayload>) => {
      state.report.isSending = false;
      if (state.link.data) state.link.data.lastReportSentAt = action.payload.sentAt;
    });
    builder.addCase(sendSeoReportThunk.rejected, state => {
      state.report.isSending = false;
    });

    builder.addCase(fetchListingsThunk.pending, state => {
      state.listings.isLoading = true;
      state.listings.hasError = false;
    });
    builder.addCase(fetchListingsThunk.fulfilled, (state, action: PayloadAction<UberallListingsSummaryPayload>) => {
      state.listings.data = action.payload;
      state.listings.isLoading = false;
      state.listings.hasLoaded = true;
    });
    builder.addCase(fetchListingsThunk.rejected, state => {
      state.listings.isLoading = false;
      state.listings.hasError = true;
      state.listings.hasLoaded = true;
    });
    builder.addCase(syncListingsThunk.pending, state => {
      state.listings.isSyncing = true;
    });
    builder.addCase(syncListingsThunk.fulfilled, (state, action: PayloadAction<UberallListingsSummaryPayload>) => {
      state.listings.data = action.payload;
      state.listings.isSyncing = false;
    });
    builder.addCase(syncListingsThunk.rejected, state => {
      state.listings.isSyncing = false;
    });

    builder.addCase(fetchPhotosThunk.pending, state => {
      state.photos.isLoading = true;
      state.photos.hasError = false;
    });
    builder.addCase(fetchPhotosThunk.fulfilled, (state, action: PayloadAction<UberallPhotosPayload>) => {
      state.photos.data = action.payload;
      state.photos.isLoading = false;
      state.photos.hasLoaded = true;
    });
    builder.addCase(fetchPhotosThunk.rejected, state => {
      state.photos.isLoading = false;
      state.photos.hasError = true;
      state.photos.hasLoaded = true;
    });
    builder.addCase(uploadPhotoThunk.pending, state => {
      state.photos.isUploading = true;
    });
    builder.addCase(uploadPhotoThunk.fulfilled, (state, action: PayloadAction<UberallPhotosPayload>) => {
      state.photos.data = action.payload;
      state.photos.isUploading = false;
    });
    builder.addCase(uploadPhotoThunk.rejected, state => {
      state.photos.isUploading = false;
    });
    builder.addCase(deletePhotoThunk.fulfilled, (state, action: PayloadAction<UberallPhotosPayload>) => {
      state.photos.data = action.payload;
    });

    builder.addCase(fetchKeywordsThunk.pending, state => {
      state.keywords.isLoading = true;
      state.keywords.hasError = false;
    });
    builder.addCase(fetchKeywordsThunk.fulfilled, (state, action: PayloadAction<UberallKeywordsPayload>) => {
      state.keywords.data = action.payload;
      state.keywords.isLoading = false;
      state.keywords.hasLoaded = true;
    });
    builder.addCase(fetchKeywordsThunk.rejected, state => {
      state.keywords.isLoading = false;
      state.keywords.hasError = true;
      state.keywords.hasLoaded = true;
    });
    builder.addCase(saveKeywordsThunk.pending, state => {
      state.keywords.isMutating = true;
    });
    builder.addCase(saveKeywordsThunk.fulfilled, (state, action: PayloadAction<UberallKeywordsPayload>) => {
      state.keywords.data = action.payload;
      state.keywords.isMutating = false;
    });
    builder.addCase(saveKeywordsThunk.rejected, state => {
      state.keywords.isMutating = false;
    });

    builder.addCase(fetchDashboardThunk.pending, (state, action) => {
      state.dashboard.isLoading = true;
      state.dashboard.hasError = false;
      state.dashboard.requestId = action.meta.requestId;
    });
    builder.addCase(fetchDashboardThunk.fulfilled, (state, action) => {
      if (state.dashboard.requestId !== action.meta.requestId) return;
      state.dashboard.data = action.payload as UberallAnalyticsPayload;
      state.dashboard.isLoading = false;
      state.dashboard.hasLoaded = true;
    });
    builder.addCase(fetchDashboardThunk.rejected, (state, action) => {
      if (state.dashboard.requestId !== action.meta.requestId) return;
      state.dashboard.isLoading = false;
      state.dashboard.hasError = true;
      state.dashboard.hasLoaded = true;
    });

    // A store switch resets the slot, so the answer for the previous store finds a new id.
    builder.addCase(fetchKeywordRankingsThunk.pending, (state, action) => {
      state.keywordRankings.isLoading = true;
      state.keywordRankings.hasError = false;
      state.keywordRankings.requestId = action.meta.requestId;
    });
    builder.addCase(fetchKeywordRankingsThunk.fulfilled, (state, action) => {
      if (state.keywordRankings.requestId !== action.meta.requestId) return;
      state.keywordRankings.data = action.payload as KeywordRankingsPayload;
      state.keywordRankings.isLoading = false;
      state.keywordRankings.hasLoaded = true;
    });
    builder.addCase(fetchKeywordRankingsThunk.rejected, (state, action) => {
      if (state.keywordRankings.requestId !== action.meta.requestId) return;
      state.keywordRankings.isLoading = false;
      state.keywordRankings.hasError = true;
      state.keywordRankings.hasLoaded = true;
    });

    // A store switch resets the slot, so the answer for the previous store finds a new id.
    builder.addCase(fetchPostDirectoriesThunk.pending, (state, action) => {
      state.postDirectories.isLoading = true;
      state.postDirectories.hasError = false;
      state.postDirectories.requestId = action.meta.requestId;
    });
    builder.addCase(fetchPostDirectoriesThunk.fulfilled, (state, action) => {
      if (state.postDirectories.requestId !== action.meta.requestId) return;
      state.postDirectories.data = action.payload as string[];
      state.postDirectories.isLoading = false;
      state.postDirectories.hasLoaded = true;
    });
    builder.addCase(fetchPostDirectoriesThunk.rejected, (state, action) => {
      if (state.postDirectories.requestId !== action.meta.requestId) return;
      state.postDirectories.isLoading = false;
      state.postDirectories.hasError = true;
      state.postDirectories.hasLoaded = true;
    });

    // Polled while the connect popup is open: the last answer stays on screen while the next one loads.
    builder.addCase(fetchSocialConnectionsThunk.pending, (state, action) => {
      state.socialConnections.isLoading = true;
      state.socialConnections.requestId = action.meta.requestId;
    });
    builder.addCase(fetchSocialConnectionsThunk.fulfilled, (state, action) => {
      if (state.socialConnections.requestId !== action.meta.requestId) return;
      // A pinned read that outlived a store switch describes another location than the one on screen.
      const pinned = action.meta.arg;
      if (pinned !== undefined && pinned !== state.link.data?.locationId) return;
      state.socialConnections.data = action.payload as UberallSocialConnectionsPayload;
      state.socialConnections.isLoading = false;
      state.socialConnections.hasError = false;
      state.socialConnections.hasLoaded = true;
    });
    builder.addCase(fetchSocialConnectionsThunk.rejected, (state, action) => {
      if (state.socialConnections.requestId !== action.meta.requestId) return;
      state.socialConnections.isLoading = false;
      state.socialConnections.hasError = true;
      state.socialConnections.hasLoaded = true;
    });
    builder.addCase(disconnectSocialThunk.pending, state => {
      state.socialConnections.isMutating = true;
    });
    builder.addCase(disconnectSocialThunk.fulfilled, (state, action) => {
      state.socialConnections.data = action.payload as UberallSocialConnectionsPayload;
      state.socialConnections.isMutating = false;
    });
    builder.addCase(disconnectSocialThunk.rejected, state => {
      state.socialConnections.isMutating = false;
    });

    builder.addCase(fetchReviewsThunk.pending, (state, action) => {
      state.reviews.isLoading = true;
      state.reviews.hasError = false;
      state.reviews.requestId = action.meta.requestId;
    });
    builder.addCase(fetchReviewsThunk.fulfilled, (state, action) => {
      if (state.reviews.requestId !== action.meta.requestId) return;
      const page = action.payload as UberallDataPointsPagePayload;
      const gone = new Set(state.reviews.goneIds);
      const hidden = page.datapoints.filter(review => gone.has(review.id));
      const listed = page.datapoints.filter(review => !gone.has(review.id));
      state.reviews.data = listed;
      state.reviews.count = Math.max(0, page.count - hidden.length);
      state.reviews.unansweredCount = Math.max(
        0,
        page.unansweredCount - hidden.filter(review => !review.replied).length,
      );
      state.reviews.hasCountLoaded = true;
      state.reviews.isLoading = false;
      state.reviews.hasLoaded = true;
      const stillListed = listed.some(review => review.id === state.reviews.selectedId);
      if (!stillListed) state.reviews.selectedId = listed[0]?.id ?? null;
    });
    builder.addCase(fetchReviewsThunk.rejected, (state, action) => {
      if (state.reviews.requestId !== action.meta.requestId) return;
      state.reviews.isLoading = false;
      state.reviews.hasError = true;
      state.reviews.hasLoaded = true;
    });

    builder.addCase(fetchUnansweredCountThunk.fulfilled, (state, action: PayloadAction<number>) => {
      state.reviews.unansweredCount = action.payload;
      state.reviews.hasCountLoaded = true;
    });
    builder.addCase(fetchUnansweredCountThunk.rejected, state => {
      state.reviews.hasCountLoaded = true;
    });

    builder.addCase(markReviewReadThunk.fulfilled, (state, action: PayloadAction<number>) => {
      const review = state.reviews.data.find(entry => entry.id === action.payload);
      if (review) review.read = true;
    });

    builder.addCase(fetchReviewReplyThunk.pending, (state, action) => {
      state.reviews.replyLoadRequestedById[action.meta.arg] = true;
    });
    builder.addCase(fetchReviewReplyThunk.fulfilled, (state, action: PayloadAction<UberallDataPointPayload>) => {
      const review = state.reviews.data.find(entry => entry.id === action.payload.id);
      if (!review) return;
      review.replied = action.payload.replied ?? review.replied;
      review.replyText = action.payload.replyText ?? review.replyText;
      review.repliedAt = action.payload.repliedAt ?? review.repliedAt;
    });

    builder.addCase(publishReplyThunk.fulfilled, (state, action: PayloadAction<number>) => {
      const datapointId = action.payload;
      const review = state.reviews.data.find(entry => entry.id === datapointId);
      if (review) {
        // Editing a reply that already exists answers nothing new, so the counter must not move.
        const wasUnanswered = !review.replied;
        review.replied = true;
        review.replyText = state.reviews.pendingReplyById[datapointId] ?? review.replyText;
        if (wasUnanswered) state.reviews.unansweredCount = Math.max(0, state.reviews.unansweredCount - 1);
      }
      delete state.reviews.pendingReplyById[datapointId];
      delete state.reviews.aiDraftById[datapointId];
      state.reviews.replyErrorById[datapointId] = false;
    });
    builder.addCase(publishReplyThunk.rejected, (state, action) => {
      const { datapointId, code } = (action.payload as { datapointId?: number; code?: string }) ?? {};
      if (!datapointId) return;
      if (code && REVIEW_GONE_CODES.includes(code)) {
        const gone = state.reviews.data.find(review => review.id === datapointId);
        state.reviews.data = state.reviews.data.filter(review => review.id !== datapointId);
        if (gone) {
          state.reviews.count = Math.max(0, state.reviews.count - 1);
          if (!gone.replied) state.reviews.unansweredCount = Math.max(0, state.reviews.unansweredCount - 1);
        }
        if (state.reviews.selectedId === datapointId) state.reviews.selectedId = state.reviews.data[0]?.id ?? null;
        // Only the reply guard's own code proves the review is gone; a bare 404 keeps the draft
        // and lets the next load bring the review back.
        if (code === 'UBERALL_REVIEW_GONE') {
          state.reviews.goneIds.push(datapointId);
          delete state.reviews.pendingReplyById[datapointId];
          delete state.reviews.aiDraftById[datapointId];
        }
        return;
      }
      state.reviews.replyErrorById[datapointId] = true;
    });

    builder.addCase(generateAiReplyThunk.pending, (state, action) => {
      state.reviews.generatingFor[action.meta.arg.datapointId] = true;
    });
    builder.addCase(generateAiReplyThunk.fulfilled, (state, action) => {
      const draft = action.payload;
      const entry: AiDraftState = {
        reply: draft.reply,
        requiresManualValidation: draft.requiresManualValidation,
        truncated: draft.truncated,
        tone: draft.tone,
        language: draft.language,
      };
      state.reviews.aiDraftById[draft.datapointId] = entry;
      state.reviews.pendingReplyById[draft.datapointId] = draft.reply;
      state.reviews.generatingFor[draft.datapointId] = false;
    });
    builder.addCase(generateAiReplyThunk.rejected, (state, action) => {
      const { datapointId } = (action.payload as { datapointId?: number }) ?? {};
      if (datapointId) state.reviews.generatingFor[datapointId] = false;
    });

    builder.addCase(fetchReplyTemplatesThunk.pending, state => {
      state.templates.isLoading = true;
      state.templates.hasError = false;
    });
    builder.addCase(
      fetchReplyTemplatesThunk.fulfilled,
      (state, action: PayloadAction<UberallReplyTemplatePayload[]>) => {
        state.templates.data = action.payload;
        state.templates.isLoading = false;
        state.templates.hasLoaded = true;
      },
    );
    builder.addCase(fetchReplyTemplatesThunk.rejected, state => {
      state.templates.isLoading = false;
      state.templates.hasError = true;
      state.templates.hasLoaded = true;
    });
    builder.addCase(saveReplyTemplateThunk.pending, state => {
      state.templates.isMutating = true;
    });
    builder.addCase(saveReplyTemplateThunk.fulfilled, (state, action: PayloadAction<UberallReplyTemplatePayload[]>) => {
      state.templates.data = action.payload;
      state.templates.isMutating = false;
    });
    builder.addCase(saveReplyTemplateThunk.rejected, state => {
      state.templates.isMutating = false;
    });
    builder.addCase(
      deleteReplyTemplateThunk.fulfilled,
      (state, action: PayloadAction<UberallReplyTemplatePayload[]>) => {
        state.templates.data = action.payload;
      },
    );

    builder.addCase(fetchServiceAreasThunk.pending, state => {
      state.serviceAreas.isLoading = true;
      state.serviceAreas.hasError = false;
    });
    builder.addCase(fetchServiceAreasThunk.fulfilled, (state, action: PayloadAction<StoreServiceAreaPayload[]>) => {
      state.serviceAreas.data = action.payload;
      state.serviceAreas.isLoading = false;
      state.serviceAreas.hasLoaded = true;
    });
    builder.addCase(fetchServiceAreasThunk.rejected, state => {
      state.serviceAreas.isLoading = false;
      state.serviceAreas.hasError = true;
      state.serviceAreas.hasLoaded = true;
    });
    builder.addCase(saveServiceAreaThunk.pending, state => {
      state.serviceAreas.isMutating = true;
    });
    builder.addCase(saveServiceAreaThunk.fulfilled, (state, action: PayloadAction<StoreServiceAreaPayload[]>) => {
      state.serviceAreas.data = action.payload;
      state.serviceAreas.isMutating = false;
    });
    builder.addCase(saveServiceAreaThunk.rejected, state => {
      state.serviceAreas.isMutating = false;
    });
    builder.addCase(deleteServiceAreaThunk.pending, state => {
      state.serviceAreas.isMutating = true;
    });
    builder.addCase(deleteServiceAreaThunk.fulfilled, (state, action: PayloadAction<StoreServiceAreaPayload[]>) => {
      state.serviceAreas.data = action.payload;
      state.serviceAreas.isMutating = false;
    });
    builder.addCase(deleteServiceAreaThunk.rejected, state => {
      state.serviceAreas.isMutating = false;
    });
    builder.addCase(syncServiceAreasToUberallThunk.pending, state => {
      state.serviceAreas.isSyncing = true;
    });
    builder.addCase(
      syncServiceAreasToUberallThunk.fulfilled,
      (state, action: PayloadAction<UberallLocationProfilePayload>) => {
        state.profile.data = action.payload;
        state.serviceAreas.isSyncing = false;
      },
    );
    builder.addCase(syncServiceAreasToUberallThunk.rejected, state => {
      state.serviceAreas.isSyncing = false;
    });

    builder.addCase(fetchAutoResponseRulesThunk.pending, state => {
      state.autoResponses.isLoading = true;
      state.autoResponses.hasError = false;
    });
    builder.addCase(
      fetchAutoResponseRulesThunk.fulfilled,
      (state, action: PayloadAction<UberallAutoResponseRuleListPayload>) => {
        state.autoResponses.data = action.payload.rules;
        state.autoResponses.count = action.payload.count;
        state.autoResponses.isLoading = false;
        state.autoResponses.hasLoaded = true;
      },
    );
    builder.addCase(fetchAutoResponseRulesThunk.rejected, state => {
      state.autoResponses.isLoading = false;
      state.autoResponses.hasError = true;
      state.autoResponses.hasLoaded = true;
    });
    builder.addCase(saveAutoResponseRuleThunk.pending, state => {
      state.autoResponses.isMutating = true;
    });
    builder.addCase(
      saveAutoResponseRuleThunk.fulfilled,
      (state, action: PayloadAction<UberallAutoResponseRuleListPayload>) => {
        state.autoResponses.data = action.payload.rules;
        state.autoResponses.count = action.payload.count;
        state.autoResponses.isMutating = false;
      },
    );
    builder.addCase(saveAutoResponseRuleThunk.rejected, state => {
      state.autoResponses.isMutating = false;
    });
    builder.addCase(deleteAutoResponseRuleThunk.pending, state => {
      state.autoResponses.isMutating = true;
    });
    builder.addCase(
      deleteAutoResponseRuleThunk.fulfilled,
      (state, action: PayloadAction<UberallAutoResponseRuleListPayload>) => {
        state.autoResponses.data = action.payload.rules;
        state.autoResponses.count = action.payload.count;
        state.autoResponses.isMutating = false;
      },
    );
    builder.addCase(deleteAutoResponseRuleThunk.rejected, state => {
      state.autoResponses.isMutating = false;
    });

    builder.addCase(fetchAiSettingsThunk.pending, state => {
      state.aiSettings.isLoading = true;
      state.aiSettings.hasError = false;
    });
    builder.addCase(fetchAiSettingsThunk.fulfilled, (state, action: PayloadAction<UberallAiSettingsPayload>) => {
      state.aiSettings.data = action.payload;
      state.aiSettings.isLoading = false;
      state.aiSettings.hasLoaded = true;
    });
    builder.addCase(fetchAiSettingsThunk.rejected, state => {
      state.aiSettings.isLoading = false;
      state.aiSettings.hasError = true;
      state.aiSettings.hasLoaded = true;
    });
    builder.addCase(saveAiSettingsThunk.pending, state => {
      state.aiSettings.isSaving = true;
    });
    builder.addCase(saveAiSettingsThunk.fulfilled, (state, action: PayloadAction<UberallAiSettingsPayload>) => {
      state.aiSettings.data = action.payload;
      state.aiSettings.isSaving = false;
    });
    builder.addCase(saveAiSettingsThunk.rejected, state => {
      state.aiSettings.isSaving = false;
    });

    builder.addCase(generateAiPostThunk.pending, state => {
      state.posts.isGeneratingDraft = true;
      state.posts.hasDraftError = false;
      state.posts.aiDraft = null;
    });
    builder.addCase(generateAiPostThunk.fulfilled, (state, action: PayloadAction<UberallAiPostPayload>) => {
      state.posts.isGeneratingDraft = false;
      if (action.payload.locationId === state.link.data?.locationId) state.posts.aiDraft = action.payload;
    });
    builder.addCase(generateAiPostThunk.rejected, state => {
      state.posts.isGeneratingDraft = false;
      state.posts.hasDraftError = true;
    });

    builder.addCase(fetchPostsThunk.pending, state => {
      state.posts.isLoading = true;
      state.posts.hasError = false;
    });
    builder.addCase(fetchPostsThunk.fulfilled, (state, action) => {
      state.posts.data = action.payload.posts;
      state.posts.count = action.payload.count;
      state.posts.isLoading = false;
      state.posts.hasLoaded = true;
    });
    builder.addCase(fetchPostsThunk.rejected, state => {
      state.posts.isLoading = false;
      state.posts.hasError = true;
      state.posts.hasLoaded = true;
    });
    builder.addCase(savePostThunk.pending, state => {
      state.posts.isMutating = true;
    });
    builder.addCase(savePostThunk.fulfilled, (state, action) => {
      state.posts.data = action.payload.posts;
      state.posts.count = action.payload.count;
      state.posts.isMutating = false;
    });
    builder.addCase(savePostThunk.rejected, state => {
      state.posts.isMutating = false;
    });
    builder.addCase(deletePostThunk.fulfilled, (state, action) => {
      state.posts.data = action.payload.posts;
      state.posts.count = action.payload.count;
    });
  },
});

export const actions = visibilitySlice.actions;
export const reducer = visibilitySlice.reducer;

export default visibilitySlice.reducer;
